import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfigProvider, Modal, Tooltip } from 'antd';
import heIL from 'antd/locale/he_IL';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ProTable, type ProColumns } from '@ant-design/pro-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faEye, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import type { CreditDateField, CreditDepositSummary, CreditPayment } from '@deposits/shared';
import { Money } from '../components/Money';
import { multiSelectColumn, numericRangeColumn } from '../components/tableHelpers';
import { EmptyState } from '../components/EmptyState';
import { Loader, loaderIndicator } from '../components/Loader';
import { TabPill } from '../components/TabPill';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { PdfDrawer } from '../features/cash/PdfDrawer';
import { NewCreditDepositDrawer } from '../features/credit/NewCreditDepositDrawer';
import { useLocationContext } from '../auth/LocationContext';
import { useProjectContext } from '../auth/ProjectContext';
import {
  useCancelCreditDeposit,
  useCreditDeposits,
  useCreditPending,
  useCreditSettings,
} from '../hooks/useCredit';
import { firstOfMonthIsrael, formatTime, isoToDisplay, lastOfMonthIsrael } from '../utils/dates';

type View = 'pending' | 'deposits';

/** "2/3" for an installment, "1" for a single payment, "—" when unknown. */
export function installmentLabel(n: number | null, total: number | null): string {
  if (n == null) return '—';
  return total != null && total > 1 ? `${n}/${total}` : String(n);
}

/** הפקדות אשראי — card installments → one company → a bank (migration 014). */
export function CreditTab() {
  const [view, setView] = useState<View>('pending');
  const [pdf, setPdf] = useState<{ id: number; number: number } | null>(null);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="pb-3">
        <TabPill<View>
          value={view}
          onChange={setView}
          options={[
            { value: 'pending', label: 'ממתינים להפקדה' },
            { value: 'deposits', label: 'הפקדות' },
          ]}
        />
      </div>
      {view === 'pending' ? (
        <PendingView
          onCreated={(id, number) => {
            setView('deposits');
            setPdf({ id, number });
          }}
        />
      ) : (
        <DepositsView onOpenPdf={(id, number) => setPdf({ id, number })} />
      )}
      <PdfDrawer
        open={pdf != null}
        title={pdf ? `העברת אשראי מספר ${pdf.number}` : ''}
        blobPath={pdf ? `/credit/deposits/${pdf.id}/pdf-blob` : null}
        downloadName={pdf ? `credit-deposit-${pdf.number}.pdf` : 'credit-deposit.pdf'}
        onClose={() => setPdf(null)}
      />
    </div>
  );
}

// ───────────────────────────────── Pending ──────────────────────────────────

function PendingView({ onCreated }: { onCreated: (id: number, number: number) => void }) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();
  const [dateField, setDateField] = useState<CreditDateField>('settlement_date');
  const [period, setPeriod] = useState(() => ({ from: firstOfMonthIsrael(), to: lastOfMonthIsrael() }));
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const params = useMemo(
    () =>
      selectedLocationId == null
        ? null
        : {
            dateField,
            from: period.from,
            to: period.to,
            location_id: selectedLocationId,
            project_id: selectedProjectId ?? undefined,
          },
    [dateField, period, selectedLocationId, selectedProjectId],
  );
  useEffect(() => setSelectedKeys([]), [selectedLocationId, selectedProjectId]);

  const { data: payments = [], isFetching, isError, refetch } = useCreditPending(params);
  const { data: settings } = useCreditSettings(selectedLocationId);
  useRegisterRefresh(useCallback(() => void refetch(), [refetch]));

  // Drop keys that left the list (deposited elsewhere, or a settings change
  // moved them to another company).
  const selected = useMemo(
    () => payments.filter((p) => selectedKeys.includes(p.external_receipt_id)),
    [payments, selectedKeys],
  );
  // One company per deposit: once a row is picked, only its company stays selectable.
  const lockedCompany = selected[0]?.company_key ?? null;
  const grouping = settings?.grouping ?? 'issuer';

  const columns: ProColumns<CreditPayment>[] = [
    {
      title: 'תאריך זיכוי',
      dataIndex: 'settlement_date',
      width: 110,
      render: (_, r) => isoToDisplay(r.settlement_date),
      sorter: (a, b) => (a.settlement_date ?? '').localeCompare(b.settlement_date ?? ''),
      defaultSortOrder: dateField === 'settlement_date' ? 'ascend' : undefined,
    },
    {
      title: 'תאריך עסקה',
      dataIndex: 'issue_date',
      width: 110,
      render: (_, r) => isoToDisplay(r.issue_date),
      sorter: (a, b) => a.issue_date.localeCompare(b.issue_date),
      defaultSortOrder: dateField === 'issue_date' ? 'ascend' : undefined,
    },
    {
      title: grouping === 'acquirer' ? 'חברה סולקת' : 'חברה מנפיקה',
      dataIndex: 'company_name',
      width: 150,
      render: (_, r) =>
        r.company_name ?? (
          <Tooltip title="שייך את החברה המנפיקה לחברה סולקת בהגדרות">
            <span className="text-error-red">לא משויך</span>
          </Tooltip>
        ),
      sorter: (a, b) => (a.company_name ?? '').localeCompare(b.company_name ?? '', 'he'),
      ...multiSelectColumn<CreditPayment>(payments, (r) => r.company_name),
    },
    ...(grouping === 'acquirer'
      ? [
          {
            title: 'מותג',
            dataIndex: 'issuer',
            width: 130,
            sorter: (a: CreditPayment, b: CreditPayment) => a.issuer.localeCompare(b.issuer, 'he'),
            ...multiSelectColumn<CreditPayment>(payments, (r) => r.issuer),
          } as ProColumns<CreditPayment>,
        ]
      : []),
    {
      title: 'מספר קבלה',
      dataIndex: 'receipt_number',
      width: 110,
      render: (_, r) =>
        r.receipt_url ? (
          <a
            href={r.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.receipt_number}
          </a>
        ) : (
          <span dir="ltr">{r.receipt_number}</span>
        ),
      sorter: (a, b) => Number(a.receipt_number) - Number(b.receipt_number),
      ...multiSelectColumn<CreditPayment>(payments, (r) => r.receipt_number),
    },
    {
      title: 'שם לקוח',
      dataIndex: 'customer_name',
      width: 180,
      ellipsis: true,
      sorter: (a, b) => a.customer_name.localeCompare(b.customer_name, 'he'),
      ...multiSelectColumn<CreditPayment>(payments, (r) => r.customer_name),
    },
    {
      title: '4 ספרות',
      dataIndex: 'card_last4',
      width: 90,
      render: (_, r) => <span dir="ltr">{r.card_last4 ?? '—'}</span>,
      ...multiSelectColumn<CreditPayment>(payments, (r) => r.card_last4),
    },
    {
      title: 'תשלום',
      dataIndex: 'installment_number',
      width: 80,
      render: (_, r) => <span dir="ltr">{installmentLabel(r.installment_number, r.installments_total)}</span>,
    },
    {
      title: 'אסמכתא',
      dataIndex: 'voucher_number',
      width: 120,
      render: (_, r) => <span dir="ltr">{r.voucher_number ?? '—'}</span>,
    },
    {
      title: 'סכום',
      dataIndex: 'amount',
      width: 110,
      render: (_, r) => <Money amount={r.amount} />,
      sorter: (a, b) => a.amount - b.amount,
      ...numericRangeColumn<CreditPayment>((r) => r.amount),
    },
  ];

  if (selectedLocationId == null) return <Loader fullScreen />;

  const total = payments.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap pb-3">
        <ReportingPeriodFilter value={period} onChange={(from, to) => setPeriod({ from, to })} />
        <TabPill<CreditDateField>
          value={dateField}
          onChange={setDateField}
          options={[
            { value: 'settlement_date', label: 'תאריך זיכוי' },
            { value: 'issue_date', label: 'תאריך עסקה' },
          ]}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
          <ProTable<CreditPayment>
            key={dateField}
            rowKey="external_receipt_id"
            columns={columns}
            dataSource={payments}
            loading={isFetching ? { spinning: true, indicator: loaderIndicator('md') } : false}
            search={false}
            options={false}
            showSorterTooltip={false}
            defaultSize="small"
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys.map(String)),
              // "Select all" only takes rows that are still enabled.
              getCheckboxProps: (r) => ({
                disabled:
                  r.company_key == null || (lockedCompany != null && r.company_key !== lockedCompany),
              }),
            }}
            pagination={tablePagination}
            locale={{
              emptyText: isError ? (
                <EmptyState title="שגיאה בטעינת תשלומי אשראי" description="נסה שוב או רענן את העמוד" />
              ) : (
                <EmptyState
                  title="אין תשלומי אשראי להפקדה"
                  description="כל התשלומים בטווח שנבחר כבר הופקדו או שאין כאלה"
                />
              ),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
            tableStyle={{ background: 'white' }}
            {...({ getPopupContainer: () => document.body } as any)}
          />
        </ConfigProvider>
      </div>

      <div
        className="bg-blue-bg border-t border-border px-2 py-3 flex items-center justify-center flex-wrap"
        style={{ gap: 24, fontSize: '0.9rem', color: 'rgb(59, 59, 59)' }}
      >
        <span>
          תשלומים בטווח{' '}
          <b dir="ltr" style={{ fontFamily: 'Ploni, sans-serif' }}>
            {payments.length}
          </b>
        </span>
        <span>
          סה"כ{' '}
          <b style={{ fontFamily: 'Ploni, sans-serif' }}>
            <Money amount={total} />
          </b>
        </span>
      </div>

      <CreditSelectionBar
        selected={selected}
        onCreate={() => setDrawerOpen(true)}
        onClear={() => setSelectedKeys([])}
      />

      <NewCreditDepositDrawer
        open={drawerOpen}
        selected={selected}
        settings={settings ?? null}
        onClose={() => setDrawerOpen(false)}
        onCreated={(id, number) => {
          setDrawerOpen(false);
          setSelectedKeys([]);
          onCreated(id, number);
        }}
      />
    </div>
  );
}

/** Floating bulk bar — same look as the receipts SelectionBar. */
function CreditSelectionBar({
  selected,
  onCreate,
  onClear,
}: {
  selected: CreditPayment[];
  onCreate: () => void;
  onClear: () => void;
}) {
  if (selected.length === 0) return null;
  const total = selected.reduce((s, p) => s + p.amount, 0);
  const divider = <div className="w-px bg-main-text shrink-0 hidden sm:block" style={{ height: 25 }} />;
  const stat = (label: string, value: React.ReactNode) => (
    <div className="flex items-center gap-2 whitespace-nowrap" style={{ fontFamily: 'Ploni, sans-serif' }}>
      <span className="text-secondary-text">{label}</span>
      <span className="text-main-text font-semibold">{value}</span>
    </div>
  );
  return createPortal(
    <div
      dir="rtl"
      className="fixed bottom-0 left-0 right-0 sm:bottom-6 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 z-50 sm:!w-[60%] sm:min-w-[640px]"
    >
      <div
        className="bg-white sm:rounded flex items-center sm:justify-between px-2 sm:px-4 py-2 sm:py-0 sm:h-[46px] gap-x-3 gap-y-2 flex-wrap sm:flex-nowrap"
        style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.16)' }}
      >
        <span className="hidden sm:inline text-main-text whitespace-nowrap" style={{ fontWeight: 600 }}>
          {selected.length} נבחרו
        </span>
        {divider}
        {stat('חברה', selected[0]!.company_name)}
        {divider}
        {stat('סה"כ', <Money amount={total} />)}
        {divider}
        <button
          onClick={onCreate}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity whitespace-nowrap py-1"
          style={{ color: '#1936f7', fontFamily: 'Ploni, sans-serif', fontWeight: 600 }}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: 14 }} />
          <span>צור הפקדה</span>
        </button>
        <button
          onClick={onClear}
          className="text-secondary-text hover:text-main-text transition-colors shrink-0 ms-auto sm:ms-0"
          aria-label="סגור"
        >
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ───────────────────────────────── Deposits ─────────────────────────────────

function DepositsView({ onOpenPdf }: { onOpenPdf: (id: number, number: number) => void }) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();
  const [period, setPeriod] = useState(() => ({ from: firstOfMonthIsrael(), to: lastOfMonthIsrael() }));
  const params = useMemo(
    () =>
      selectedLocationId == null
        ? null
        : {
            location_id: selectedLocationId,
            project_id: selectedProjectId ?? undefined,
            from: period.from,
            to: period.to,
          },
    [selectedLocationId, selectedProjectId, period],
  );
  const { data: deposits = [], isFetching, isError, refetch } = useCreditDeposits(params);
  useRegisterRefresh(useCallback(() => void refetch(), [refetch]));
  const cancel = useCancelCreditDeposit();

  const confirmCancel = (d: CreditDepositSummary) =>
    Modal.confirm({
      title: `ביטול העברת אשראי מספר ${d.deposit_number}`,
      content: (
        <div className="text-small">
          <div>הביטול ישחרר את התשלומים בחזרה לרשימת הממתינים להפקדה.</div>
          <div className="mt-2 text-secondary-text">
            המספר {d.deposit_number} לא ישמש שוב — הוא יישאר רשום כמבוטל. אי אפשר להפוך את הפעולה.
          </div>
        </div>
      ),
      okText: 'בטל הפקדה',
      cancelText: 'חזרה',
      okButtonProps: { danger: true },
      direction: 'rtl',
      onOk: async () => {
        try {
          await cancel.mutateAsync({ id: d.id });
          toast.success(`הפקדה מספר ${d.deposit_number} בוטלה`);
        } catch (err) {
          toast.error((err as Error).message || 'שגיאה בביטול ההפקדה');
          throw err;
        }
      },
    });

  const columns: ProColumns<CreditDepositSummary>[] = [
    {
      title: 'תאריך',
      dataIndex: 'deposit_date',
      width: 110,
      render: (_, r) => isoToDisplay(r.deposit_date),
      sorter: (a, b) => a.deposit_date.localeCompare(b.deposit_date),
    },
    {
      title: 'שעת יצירה',
      dataIndex: 'created_at',
      width: 90,
      render: (_, r) => formatTime(r.created_at),
    },
    {
      title: 'מספר',
      dataIndex: 'deposit_number',
      width: 80,
      render: (_, r) => <span dir="ltr">{r.deposit_number}</span>,
      sorter: (a, b) => a.deposit_number - b.deposit_number,
      defaultSortOrder: 'descend',
    },
    {
      title: 'מחברה',
      dataIndex: 'company_name',
      width: 170,
      render: (_, r) => (
        <span>
          {r.company_name} <span className="text-secondary-text" dir="ltr">({r.company_ledger_account})</span>
        </span>
      ),
      ...multiSelectColumn<CreditDepositSummary>(deposits, (r) => r.company_name),
    },
    {
      title: 'לבנק',
      dataIndex: 'bank_name',
      width: 170,
      render: (_, r) => (
        <span>
          {r.bank_name} <span className="text-secondary-text" dir="ltr">({r.bank_ledger_account})</span>
        </span>
      ),
      ...multiSelectColumn<CreditDepositSummary>(deposits, (r) => r.bank_name),
    },
    {
      title: 'תשלומים',
      dataIndex: 'payment_count',
      width: 80,
      render: (_, r) => <span dir="ltr">{r.payment_count}</span>,
    },
    {
      title: 'סכום',
      dataIndex: 'total',
      width: 120,
      render: (_, r) => (
        <span className="font-semibold">
          <Money amount={r.total} />
        </span>
      ),
      sorter: (a, b) => a.total - b.total,
      ...numericRangeColumn<CreditDepositSummary>((r) => r.total),
    },
    {
      title: 'סטטוס',
      dataIndex: 'cancelled_at',
      width: 80,
      render: (_, r) =>
        r.cancelled_at ? (
          <span className="text-error-red font-semibold">מבוטל</span>
        ) : (
          <span className="text-success-green">הופקד</span>
        ),
    },
    {
      title: 'פקודת יומן',
      dataIndex: 'journal_status',
      width: 130,
      render: (_, r) => {
        switch (r.journal_status) {
          case 'synced':
            return <span className="text-success-green">נשלחה</span>;
          case 'cancelled':
            return <span className="text-secondary-text">בוטלה ב-Arbox</span>;
          case 'pending':
          case 'cancel_pending':
            return <span className="text-secondary-text">בשליחה…</span>;
          case 'failed':
          case 'cancel_failed':
            return (
              <Tooltip title={r.journal_last_error || 'השליחה ל-Arbox נכשלה. המערכת תנסה שוב.'}>
                <span className="text-error-red">
                  {r.journal_status === 'failed' ? 'השליחה נכשלה' : 'הביטול נכשל'}
                </span>
              </Tooltip>
            );
          default:
            return <span className="text-secondary-text">לא נשלחה</span>;
        }
      },
    },
    {
      title: 'יוצר',
      dataIndex: 'created_by_name',
      width: 150,
      ellipsis: true,
      render: (_, r) => r.created_by_name || '—',
    },
    {
      title: 'הערות',
      dataIndex: 'notes',
      width: 160,
      ellipsis: true,
      render: (_, r) => r.notes || '—',
    },
    {
      title: 'פעולות',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, r) => (
        <div className="flex items-center gap-3">
          <ActionLink icon={faEye} label="צפה" onClick={() => onOpenPdf(r.id, r.deposit_number)} />
          <ActionLink
            icon={faBan}
            label="ביטול"
            danger
            disabled={r.cancelled_at != null}
            onClick={() => confirmCancel(r)}
          />
        </div>
      ),
    },
  ];

  if (selectedLocationId == null) return <Loader fullScreen />;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap pb-3">
        <ReportingPeriodFilter
          value={period}
          onChange={(from, to) => setPeriod({ from, to })}
          buttonPrefix="תאריך הפקדה"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
          <ProTable<CreditDepositSummary>
            rowKey="id"
            columns={columns}
            dataSource={deposits}
            loading={isFetching ? { spinning: true, indicator: loaderIndicator('md') } : false}
            search={false}
            options={false}
            showSorterTooltip={false}
            defaultSize="small"
            pagination={tablePagination}
            locale={{
              emptyText: isError ? (
                <EmptyState title="שגיאה בטעינת הפקדות" description="נסה שוב או רענן את העמוד" />
              ) : (
                <EmptyState
                  title="אין הפקדות אשראי בטווח שנבחר"
                  description="צור הפקדה מרשימת הממתינים להפקדה"
                />
              ),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
            tableStyle={{ background: 'white' }}
            {...({ getPopupContainer: () => document.body } as any)}
          />
        </ConfigProvider>
      </div>
    </div>
  );
}

const tablePagination = {
  defaultPageSize: 50,
  showSizeChanger: true,
  pageSizeOptions: ['25', '50', '100', '200'],
  size: 'small' as const,
  position: ['bottomLeft' as const],
  className: 'he-pagination',
  itemRender: (_page: number, type: string, original: React.ReactNode) => {
    if (type === 'prev')
      return (
        <button type="button" className="ant-pagination-item-link">
          <RightOutlined />
        </button>
      );
    if (type === 'next')
      return (
        <button type="button" className="ant-pagination-item-link">
          <LeftOutlined />
        </button>
      );
    return original;
  },
  locale: heIL.Pagination,
};

function ActionLink({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 text-small font-medium transition-opacity ${
        disabled
          ? 'text-secondary-text cursor-not-allowed opacity-60'
          : danger
            ? 'text-error-red hover:opacity-70'
            : 'text-main-text hover:opacity-70'
      }`}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
      <span>{label}</span>
    </button>
  );
}
